import React, { useState, useEffect, useCallback } from 'react';
import { 
    Table, 
    Button, 
    Dropdown, 
    Menu, 
    Modal, 
    Tooltip, 
    Input,
    Select,
    Checkbox,
    Radio,
    Popover,
    Typography,
    Space,
    Row,
    Col,
    Pagination
} from 'antd';
import { 
    MoreOutlined, 
    CopyOutlined, 
    SearchOutlined,
    FilterOutlined,
    MailOutlined,
    EditOutlined,
    DeleteOutlined,
    CaretDownOutlined,
    FilterFilled,
    CloseOutlined
} from '@ant-design/icons';
import apiCall from '../app/API/apiService';
import apiEndpoints from '../app/API/apiEndpoints';
import { toasty } from '../Helper';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import './applicantDataTableStyle.css';
import cancelIcon from "../app/assets/svgComponent/cancel.svg";
import saveIcon from "../app/assets/svgComponent/save.svg";
import FilterIcon from '../app/assets/svgComponent/filter.svg';
import YourSVG from '../app/assets/svgComponent/scheduleinterview.svg'; // Import your SVG

const ApplicantDataTable = () => {
    const navigate = useNavigate();
    const [applicants, setApplicants] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [pageSize, setPageSize] = useState(15);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredApplicants, setFilteredApplicants] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [filters, setFilters] = useState(() => {
        const savedFilters = localStorage.getItem('applicantFilters');
        return savedFilters ? JSON.parse(savedFilters) : {
            org_id: '',
            department_id: '',
            subdepartment_id: '',
            role_id: '',
            job_opening_id: '',
            ai_score_min: '',
            ai_score_max: ''
        };
    });
    const [isFiltered, setIsFiltered] = useState(() => {
        const savedFilters = localStorage.getItem('applicantFilters');
        if (savedFilters) {
            const parsedFilters = JSON.parse(savedFilters);
            return Object.values(parsedFilters).some(value => value);
        }
        return false;
    });
    const [departments, setDepartments] = useState([]);
    const [subDepartments, setSubDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [jobOpenings, setJobOpenings] = useState([]);
    const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
    const [filteredRoles, setFilteredRoles] = useState([]);
    const [filteredJobOpenings, setFilteredJobOpenings] = useState([]);
    const [selectionMode, setSelectionMode] = useState('none');
    const [selectedApplicants, setSelectedApplicants] = useState([]);
    const [selectionMenuAnchor, setSelectionMenuAnchor] = useState(null);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const openFilter = Boolean(filterAnchorEl);
    const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
    const [allApplicantsIds, setAllApplicantsIds] = useState([]);
    const [isLoadingAllIds, setIsLoadingAllIds] = useState(false);
    const [interviewStatusAnchorEl, setInterviewStatusAnchorEl] = useState(null);
    const [selectedInterviewRow, setSelectedInterviewRow] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [filteredDepartments, setFilteredDepartments] = useState([]);
    const [filterRef, setFilterRef] = useState(null);
    const [selectedFilters, setSelectedFilters] = useState({
        org_id: "",
        department_id: "",
        subdepartment_id: "",
        role_id: "",
        job_opening_id: "",
        ai_score_min: "",
        ai_score_max: ""
    });

    useEffect(() => {
        localStorage.setItem('applicantFilters', JSON.stringify(filters));
    }, [filters]);

    const fetchApplicants = useCallback(async () => {
        setIsLoading(true);
        try {
            const hasActiveFilters = Object.values(filters).some(value => value);
            let url;
            
            if (searchQuery.trim()) {
                url = `${apiEndpoints.APPLICANTS_SEARCH}?query=${encodeURIComponent(searchQuery.trim())}&searchType=startsWith&page=${page}&page_size=${pageSize}`;
            } else if (hasActiveFilters) {
                // If AI score filters are active, fetch all pages
                if (filters.ai_score_min !== '' || filters.ai_score_max !== '') {
                    const filterParams = Object.entries(filters)
                        .filter(([key, value]) => value !== '')
                        .map(([key, value]) => `${key}=${value}`)
                        .join('&');
                    
                    url = `${apiEndpoints.APPLICANTS_FILTER}?page=${page}&page_size=${pageSize}&${filterParams}`;
                    
                    const response = await apiCall("get", url, {});
                    
                    if (response && response.items) {
                        // Apply AI score filtering on the frontend
                        const minScore = filters.ai_score_min !== '' ? parseFloat(filters.ai_score_min) : -Infinity;
                        const maxScore = filters.ai_score_max !== '' ? parseFloat(filters.ai_score_max) : Infinity;
                        
                        const filteredItems = response.items.filter(applicant => {
                            const score = parseFloat(applicant.ai_score || 0);
                            return score >= minScore && score <= maxScore;
                        });
                        
                        setApplicants(filteredItems);
                        setFilteredApplicants(filteredItems);
                        setTotalPages(Math.ceil(response.total / pageSize));
                        setTotalItems(response.total);
                    }
                    
                    setIsLoading(false);
                    return;
                }

                // Normal filtering without AI score
                const filterParams = Object.entries(filters)
                    .filter(([_, value]) => value !== '')
                    .map(([key, value]) => `${key}=${value}`)
                    .join('&');
                
                url = `${apiEndpoints.APPLICANTS_FILTER}?page=${page}&page_size=${pageSize}`;
                if (filterParams) {
                    url += `&${filterParams}`;
                }
            } else {
                url = `${apiEndpoints.CANDIDATE_DETAILS}?page=${page}&page_size=${pageSize}`;
            }
            
            // For non-AI score filters, fetch normally
            if (!filters.ai_score_min && !filters.ai_score_max) {
                const response = await apiCall("get", url, {});
                
                if (response && response.items) {
                    setApplicants(response.items);
                    setFilteredApplicants(response.items);
                    setTotalPages(Math.ceil(response.total / pageSize));
                    setTotalItems(response.total);
                } else {
                    toasty('Failed to fetch applicants', 'error');
                }
            }
        } catch (error) {
            console.error('Error fetching applicants:', error);
            toasty('Error fetching applicants', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize, filters, searchQuery]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchApplicants();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [page, searchQuery, filters, pageSize, fetchApplicants]);

    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const response = await apiCall("get", apiEndpoints.ORGANIZATION_SUMMARY, {});
                if (response) {
                    setOrganizations(response);
                }
            } catch (error) {
                console.error('Error fetching organizations:', error);
                toasty('Error fetching organizations', 'error');
            }
        };

        // Fetch organizations when filter panel is opened
        if (openFilter) {
            fetchOrganizations();
        }
    }, [openFilter]);

    useEffect(() => {
        const fetchDepartments = async () => {
            if (selectedFilters.org_id) {
                try {
                    const response = await apiCall(
                        "get", 
                        `${apiEndpoints.DEPARTMENT_SUMMARY}?organisation_id=${selectedFilters.org_id}`, 
                        {}
                    );
                    if (response && Array.isArray(response)) {
                        setFilteredDepartments(response);
                    } else if (response?.items && Array.isArray(response.items)) {
                        setFilteredDepartments(response.items);
                    } else {
                        console.error("Invalid department data format:", response);
                        setFilteredDepartments([]);
                    }
                } catch (error) {
                    console.error('Error fetching departments:', error);
                    toasty('Error fetching departments', 'error');
                }
            }
        };

        fetchDepartments();
    }, [selectedFilters.org_id]);

    useEffect(() => {
        const fetchSubDepartments = async () => {
            if (selectedFilters.org_id && selectedFilters.department_id) {
                try {
                    const response = await apiCall(
                        "get", 
                        `${apiEndpoints.SUBDEPARTMENT_SUMMARY}?organisation_id=${selectedFilters.org_id}&department_id=${selectedFilters.department_id}`, 
                        {}
                    );
                    if (response && Array.isArray(response)) {
                        setFilteredSubDepartments(response);
                    } else if (response?.items && Array.isArray(response.items)) {
                        setFilteredSubDepartments(response.items);
                    } else {
                        console.error("Invalid subdepartment data format:", response);
                        setFilteredSubDepartments([]);
                    }
                } catch (error) {
                    console.error('Error fetching sub-departments:', error);
                    toasty('Error fetching sub-departments', 'error');
                }
            }
        };

        fetchSubDepartments();
    }, [selectedFilters.org_id, selectedFilters.department_id]);

    useEffect(() => {
        const fetchRoles = async () => {
            if (selectedFilters.org_id && selectedFilters.department_id && selectedFilters.subdepartment_id) {
                try {
                    const response = await apiCall(
                        "get", 
                        `${apiEndpoints.ROLE_SUMMARY}?org_id=${selectedFilters.org_id}&department_id=${selectedFilters.department_id}&subdepartment_id=${selectedFilters.subdepartment_id}`, 
                        {}
                    );
                    if (response && Array.isArray(response)) {
                        setFilteredRoles(response);
                    } else if (response?.items && Array.isArray(response.items)) {
                        setFilteredRoles(response.items);
                    } else {
                        console.error("Invalid roles data format:", response);
                        setFilteredRoles([]);
                    }
                } catch (error) {
                    console.error('Error fetching roles:', error);
                    toasty('Error fetching roles', 'error');
                }
            }
        };

        fetchRoles();
    }, [selectedFilters.org_id, selectedFilters.department_id, selectedFilters.subdepartment_id]);

    useEffect(() => {
        const fetchJobOpenings = async () => {
            if (selectedFilters.org_id && selectedFilters.department_id && 
                selectedFilters.subdepartment_id && selectedFilters.role_id) {
                try {
                    const response = await apiCall(
                        "get", 
                        `${apiEndpoints.JOBOPENING_SUMMARY}?org_id=${selectedFilters.org_id}&department_id=${selectedFilters.department_id}&subdepartment_id=${selectedFilters.subdepartment_id}&role_id=${selectedFilters.role_id}`, 
                        {}
                    );
                    if (response && Array.isArray(response)) {
                        setFilteredJobOpenings(response);
                    } else if (response?.items && Array.isArray(response.items)) {
                        setFilteredJobOpenings(response.items);
                    } else {
                        console.error("Invalid job openings data format:", response);
                        setFilteredJobOpenings([]);
                    }
                } catch (error) {
                    console.error('Error fetching job openings:', error);
                    toasty('Error fetching job openings', 'error');
                }
            }
        };

        fetchJobOpenings();
    }, [selectedFilters.org_id, selectedFilters.department_id, selectedFilters.subdepartment_id, selectedFilters.role_id]);

    const handlePageChange = (newPage) => {
        // Only change page if we have results
        if (filteredApplicants.length > 0 || totalItems > 0) {
            setPage(newPage);
        }
    };

    const handleCopyText = (text) => {
        navigator.clipboard.writeText(text);
        toasty('Copied to clipboard!', 'success');
    };

    const handleActionClick = (event, applicant) => {
        setAnchorEl(event.currentTarget);
        setSelectedApplicant(applicant);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedApplicant(null);
    };

    const handleEdit = () => {
        console.log('Edit applicant:', selectedApplicant);
        handleClose();
    };

    const handleDelete = (record) => {
        setSelectedApplicant(record);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedApplicant?.id) {
            console.error('No applicant selected for deletion');
            return;
        }

        try {
            setIsLoading(true);
            console.log('Deleting applicant:', selectedApplicant);

            await apiCall(
                "delete",
                `${apiEndpoints.DELETE_APPLICANT}/${selectedApplicant.id}`
            );

            setApplicants(prev => prev.filter(a => a.id !== selectedApplicant.id));
            setFilteredApplicants(prev => prev.filter(a => a.id !== selectedApplicant.id));
            setTotalItems(prev => prev - 1);

            toasty('Applicant deleted successfully', 'success');
        } catch (error) {
            console.error('Error deleting applicant:', error);
            toasty('Failed to delete applicant', 'error');
        } finally {
            setIsLoading(false);
            setDeleteDialogOpen(false);
            setSelectedApplicant(null);
        }
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
        setSelectedApplicant(null);
    };

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchQuery(value);
        
        if (page !== 1) {
            setPage(1);
        }
    };

    const handleRowClick = (applicant, event) => {
        // Check if the click is not on the actions column
        if (!event.target.closest('.ant-dropdown') && 
            !event.target.closest('.ant-dropdown-trigger') && 
            !event.target.closest('.ant-btn')) {
            navigate(`/candidate-details?applicant_id=${applicant.id}`);
        }
    };

    const handleFilterChange = (event) => {
        const { name, value } = event.target;

        setSelectedFilters(prev => {
            const isSelected = prev[name] === value;
            const newFilters = {
                ...prev,
                [name]: isSelected ? "" : value // Unselect if already selected
            };
            
            // Clear dependent filters when parent filter changes
            if (name === 'org_id' && !isSelected) {
                newFilters.department_id = '';
                newFilters.subdepartment_id = '';
                newFilters.role_id = '';
                newFilters.job_opening_id = '';
            } else if (name === 'department_id' && !isSelected) {
                newFilters.subdepartment_id = '';
                newFilters.role_id = '';
                newFilters.job_opening_id = '';
            } else if (name === 'subdepartment_id' && !isSelected) {
                newFilters.role_id = '';
                newFilters.job_opening_id = '';
            } else if (name === 'role_id' && !isSelected) {
                newFilters.job_opening_id = '';
            }
            
            return newFilters;
        });
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            org_id: '',
            department_id: '',
            subdepartment_id: '',
            role_id: '',
            job_opening_id: '',
            ai_score_min: '',
            ai_score_max: ''
        };
        setSelectedFilters(clearedFilters);
        setIsFiltered(false);
        
        // Apply the cleared filters immediately to refresh the data
        setFilters(clearedFilters);
        setPage(1);
        
        // Close the filter panel
        setFilterAnchorEl(null);
        
        // Update localStorage
        localStorage.setItem('applicantFilters', JSON.stringify(clearedFilters));
    };

  

    useEffect(() => {
        if (selectedFilters.org_id) {
            const depts = departments.filter(dept => 
                dept.organisation_id === selectedFilters.org_id || 
                dept.organization_id === selectedFilters.org_id
            );
            setFilteredDepartments(depts);
        } else {
            setFilteredDepartments([]);
            setFilteredSubDepartments([]);
            setFilteredRoles([]);
            setFilteredJobOpenings([]);
        }
    }, [selectedFilters.org_id, departments]);

    useEffect(() => {
        if (selectedFilters.department_id) {
            const subdepts = subDepartments.filter(subdept => 
                subdept.department_id === selectedFilters.department_id
            );
            setFilteredSubDepartments(subdepts);
        } else {
            setFilteredSubDepartments([]);
            setFilteredRoles([]);
            setFilteredJobOpenings([]);
        }
    }, [selectedFilters.department_id, subDepartments]);

    useEffect(() => {
        if (selectedFilters.subdepartment_id) {
            const filteredRolesList = roles.filter(role => 
                role.subdepartment_id === selectedFilters.subdepartment_id
            );
            console.log("Filtered roles:", filteredRolesList); // Debug log
            setFilteredRoles(filteredRolesList);
        } else {
            setFilteredRoles([]);
            setFilteredJobOpenings([]);
        }
    }, [selectedFilters.subdepartment_id, roles]);

    useEffect(() => {
        if (selectedFilters.role_id) {
            const filteredJobs = jobOpenings.filter(job => 
                job.role_id === selectedFilters.role_id
            );
            setFilteredJobOpenings(filteredJobs);
        } else {
            setFilteredJobOpenings([]);
        }
    }, [selectedFilters.role_id, jobOpenings]);

    useEffect(() => {
        return () => {
            // Uncomment the next line if you want to clear filters when leaving the page
            // localStorage.removeItem('applicantFilters');
        };
    }, []);

    const handleRowsPerPageChange = (event) => {
        const newPageSize = parseInt(event.target.value, 15);
        setPageSize(newPageSize);
        setPage(1); // Reset to first page when changing page size
    };

    const handleSelectionMenuClick = (event) => {
        setSelectionMenuAnchor(event.currentTarget);
    };

    const handleSelectionMenuClose = () => {
        setSelectionMenuAnchor(null);
    };

    const handleSelectionModeChange = async (mode) => {
        setSelectionMode(mode);
        if (mode === 'all') {
            const ids = await fetchAllApplicantIds();
            setSelectedApplicants(ids);
        } else if (mode === 'none' || mode === 'manual') {
            setSelectedApplicants([]);
        }
        handleSelectionMenuClose();
    };

    const handleApplicantSelection = (e, applicantId) => {
        e.stopPropagation();
        
        setSelectedApplicants(prev => {
            if (prev.includes(applicantId)) {
                return prev.filter(id => id !== applicantId);
            } else {
                return [...prev, applicantId];
            }
        });
    };

    const handleSendEmails = async () => {
        if (selectedApplicants.length === 0) {
            toasty('Please select at least one applicant', 'warning');
            return;
        }

        setIsSendingEmail(true);
        try {
            const response = await apiCall(
                'post',
                apiEndpoints.SEND_INTERVIEW_EMAILS,
                {
                    applicant_ids: selectedApplicants
                }
            );

            if (response) {
                toasty('Emails sent successfully', 'success');
                // Reset selections after successful send
                setSelectionMode('none');
                setSelectedApplicants([]);
            } else {
                toasty('Failed to send emails', 'error');
            }
        } catch (error) {
            console.error('Error sending emails:', error);
            toasty('Error sending emails', 'error');
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Select all applicants
            setSelectedApplicants(filteredApplicants.map(applicant => applicant.id));
        } else {
            // Deselect all applicants
            setSelectedApplicants([]);
        }
    };

    const handleEmailButtonClick = () => {
        setEmailConfirmOpen(true);
    };

    const handleEmailConfirmClose = () => {
        setEmailConfirmOpen(false);
    };

    const handleEmailConfirm = () => {
        handleSendEmails();
        setEmailConfirmOpen(false);
    };

    const fetchAllApplicantIds = async () => {
        setIsLoadingAllIds(true);
        try {
            const MAX_PAGE_SIZE = 100;
            let allIds = [];
            let currentPage = 1;
            let hasMorePages = true;

            while (hasMorePages) {
                let url;
                if (searchQuery.trim()) {
                    url = `${apiEndpoints.APPLICANTS_SEARCH}?query=${encodeURIComponent(searchQuery.trim())}&searchType=startsWith&page=${currentPage}&page_size=${MAX_PAGE_SIZE}`;
                } else if (Object.values(filters).some(value => value)) {
                    const filterParams = Object.entries(filters)
                        .filter(([_, value]) => value !== '')
                        .map(([key, value]) => `${key}=${value}`)
                        .join('&');
                    url = `${apiEndpoints.APPLICANTS_FILTER}?page=${currentPage}&page_size=${MAX_PAGE_SIZE}&${filterParams}`;
                } else {
                    url = `${apiEndpoints.CANDIDATE_DETAILS}?page=${currentPage}&page_size=${MAX_PAGE_SIZE}`;
                }

                const response = await apiCall("get", url, {});
                
                if (response?.items?.length) {
                    const pageIds = response.items.map(item => item.id);
                    allIds = [...allIds, ...pageIds];
                    
                    hasMorePages = response.items.length === MAX_PAGE_SIZE;
                    currentPage++;
                } else {
                    hasMorePages = false;
                }
            }

            setAllApplicantsIds(allIds);
            return allIds;
        } catch (error) {
            console.error('Error fetching all applicant IDs:', error);
            toasty('Error fetching all applicants', 'error');
            return [];
        } finally {
            setIsLoadingAllIds(false);
        }
    };

    const handleInterviewStatusClick = (event, applicant) => {
        event.stopPropagation();
        setInterviewStatusAnchorEl(event.currentTarget);
        setSelectedInterviewRow(applicant);
    };

    const handleInterviewStatusClose = () => {
        setInterviewStatusAnchorEl(null);
        setSelectedInterviewRow(null);
    };

    const applyFilters = () => {
        setFilters(selectedFilters);
        setIsFiltered(Object.values(selectedFilters).some(value => value !== ''));
        setPage(1);
        
        // Close the filter panel
        setFilterAnchorEl(null);
        
        // Update localStorage
        localStorage.setItem('applicantFilters', JSON.stringify(selectedFilters));
    
        // Close the filter modal
        setFilterAnchorEl(null);
    };
    

    // Add columns configuration
    const columns = [
        selectionMode !== 'none' && {
            title: '',
            width: 48,
            render: (_, record) => (
                <Checkbox
                    checked={selectedApplicants.includes(record.id)}
                    onChange={(e) => handleApplicantSelection(e, record.id)}
                    onClick={(e) => e.stopPropagation()}
                />
            )
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>ID</span>,
            dataIndex: 'id',
            render: (text) => (
                <>
                    {text.slice(-10)}
                    <Tooltip title="Copy ID">
                        <Button
                            icon={<CopyOutlined />}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopyText(text);
                            }}
                            style={{ marginLeft: 8 }}
                            
                        />
                    </Tooltip>
                </>
            )
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500',        whiteSpace: "nowrap",  /* Prevent line break */
            }}>Created At</span>,
            dataIndex: 'created_at',
            render: (text) => text ? moment.utc(text).tz('Asia/Kolkata').format('DD/MM/YYYY, HH:mm') : 'N/A'
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Name</span>,
            dataIndex: 'candidate_name'
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Phone</span>,
            dataIndex: 'candidate_phone'
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Email</span>,
            dataIndex: 'candidate_email'
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Organization</span>,
            dataIndex: 'organisation_name',
            render: (text) => text || 'N/A'
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Department</span>,
            dataIndex: 'department_name',
            render: (text) => text || 'N/A'
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500',        whiteSpace: "nowrap",  /* Prevent line break */
            }}>Sub-Department</span>,
            dataIndex: 'subdepartment_name',
            render: (text) => text || 'N/A'
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Role</span>,
            dataIndex: 'role_name',
            render: (text) => text || 'N/A'
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500',        whiteSpace: "nowrap",  /* Prevent line break */
            }}>Job Opening name</span>,
            dataIndex: 'jobopening_name',
            render: (text) => text || 'N/A'
        },
       
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500',        whiteSpace: "nowrap",  /* Prevent line break */
            }}>CV Score</span>,
            dataIndex: 'ai_score',
            render: (text) => text ? text : '0.0'
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500',whiteSpace: "nowrap",  /* Prevent line break */
            }}>CV Summary</span>,
            dataIndex: 'ai_summary',
            render: (text) => (
                <Tooltip title={text || 'No summary available'}>
                    <span>{text ? (text.length > 20 ? `${text.slice(0, 20)}...` : text) : 'No summary available'}</span>
                </Tooltip>
            )
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500',whiteSpace: "nowrap",  /* Prevent line break */
            }}>CV Source</span>,
            dataIndex: 'resume_source',
            render: (text) => (
                <Tooltip title={text || 'No Source available'}>
                    <span>{text ? (text.length > 20 ? `${text.slice(0, 20)}...` : text) : 'No Source available'}</span>
                </Tooltip>
            )
        },


        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500',        whiteSpace: "nowrap",  /* Prevent line break */
            }}>Interview Status</span>,
            dataIndex: 'interview_status',
            render: (status) => {
                if (!status || Object.keys(status).length === 0) {
                    return <span style={{ color: "#6B7280" }}>No Status</span>;
                }
                
                // Find the latest round (highest round number)
                const rounds = Object.keys(status).map(round => parseInt(round));
                const latestRound = Math.max(...rounds);
                const latestStatus = status[latestRound.toString()];
                
                // Return the formatted status with round information
                return (
                    <Tooltip title={
                        <>
                            {Object.entries(status).map(([round, value]) => (
                                <div key={round}>Round {round}: <strong>{value}</strong></div>
                            ))}
                        </>
                    }>
                        <span>
                            Round {latestRound}: <strong>{latestStatus}</strong>
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Actions</span>,
            key: 'actions',
            render: (_, record) => (
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item 
                                key="delete" 
                                onClick={() => {
                                    handleDelete(record);
                                    return false; // Prevent event bubbling
                                }}
                                icon={<DeleteOutlined />}
                                danger
                            >
                                Delete
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={['click']}
                    onClick={e => {
                        if (e && e.stopPropagation) {
                            e.stopPropagation();
                        }
                    }}
                >
                    <Button 
                        icon={<MoreOutlined />}
                        onClick={e => {
                            if (e && e.stopPropagation) {
                                e.stopPropagation();
                            }
                        }}
                    />
                </Dropdown>
            )
        }
    ].filter(Boolean);

    return (
        <div className="applicant-data-container" style={{ overflowX: 'auto' }}>
            <div style={{ width: "auto", minWidth: "1200px" }}>
                <div className="search-section">
                    <Row 
                        justify="space-between" 
                        align="middle" 
                        style={{ 
                            marginBottom: 8,
                            padding: 16,
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Col>
                            {selectionMode !== 'none' && selectedApplicants.length > 0 && (
                                <Button
                                    type="primary"
                                    icon={<MailOutlined />}
                                    onClick={handleEmailButtonClick}
                                    disabled={isSendingEmail}
                                >
                                    {isSendingEmail ? 'Sending...' : `Send Email${selectedApplicants.length > 1 ? 's' : ''} (${selectedApplicants.length})`}
                                </Button>
                            )}
                        </Col>

                        <Col style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Input
                                placeholder="Search by email or phone..."
                                prefix={<SearchOutlined />}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                style={{ width: 250 }}
                            />

                            <Button
                                onClick={handleFilterClick}
                                type={isFiltered ? "primary" : "default"}
                                style={{ border: 'none', background: 'transparent', padding: 0, display: 'flex', alignItems: 'center', marginRight:'-10px' }}
                            >
                                <img 
                                    src={FilterIcon} 
                                    alt="Filter" 
                                    style={{ width: '155px', height: '32px' }} 
                                />
                                
                            </Button>

                            {/* <Dropdown
                                overlay={
                                    <Menu>
                                        <Menu.Item onClick={() => handleSelectionModeChange('all')}>
                                            <Radio checked={selectionMode === 'all'}>
                                                Email All Applicants
                                            </Radio>
                                        </Menu.Item>
                                        <Menu.Item onClick={() => handleSelectionModeChange('manual')}>
                                            <Radio checked={selectionMode === 'manual'}>
                                                Choose Recipients
                                            </Radio>
                                        </Menu.Item>
                                        <Menu.Item onClick={() => handleSelectionModeChange('none')}>
                                            <Radio checked={selectionMode === 'none'}>
                                                Clear Recipients
                                            </Radio>
                                        </Menu.Item>
                                    </Menu>
                                }
                                trigger={['click']}
                            >
                                <img src={YourSVG} alt="Select Recipients" style={{ cursor: 'pointer', height:"32px" , width:'155px' }} />
                            </Dropdown> */}
                        </Col>
                    </Row>
                </div>

                <Table
                    columns={columns}
                    dataSource={filteredApplicants}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    rowClassName="custom-row"
                    className="antTableClass"
                    scroll={{ x: 2000 , y: "60vh"}}
                    onRow={(record) => ({
                        onClick: (event) => handleRowClick(record, event),
                        style: { cursor: 'pointer' }
                    })}
                    footer={() => (
                        <Row 
                            justify="space-between" 
                            align="middle" 
                            style={{ 
                                backgroundColor: 'rgba(238, 238, 240, 1)',
                                borderTop: '0.5px solid rgba(218, 218, 221, 1)',
                                margin: -16,
                                padding: 16,
                                width: 'calc(100% + 32px)',
                            }}
                        >
                            <Col>
                                <div style={{ 
                                    fontFamily: 'Inter', 
                                    fontSize: '12px', 
                                    fontWeight: 400, 
                                    lineHeight: '18px', 
                                    letterSpacing: '-0.1px', 
                                    color: '#27272A'    
                                }}>
                                    {filteredApplicants.length === 0 && !isLoading 
                                        ? "No results found" 
                                        : `Showing ${Math.min(filteredApplicants.length, pageSize)} of ${totalItems} Applicants`
                                    }
                                </div>
                            </Col>
                            <Col>
                                <Space direction="vertical" align="center">
                                    <Pagination
                                        current={page}
                                        total={totalItems}
                                        pageSize={pageSize}
                                        showSizeChanger={false}
                                        size="small"
                                        style={{ margin: '0 auto' }}
                                        onChange={handlePageChange}
                                        itemRender={(_, type, originalElement) => {
                                            if (type === 'prev' || type === 'next') {
                                                return null;
                                            }
                                            return originalElement;
                                        }}
                                        className="custom-pagination"
                                        disabled={filteredApplicants.length === 0 && !isLoading}
                                    />
                                </Space>
                            </Col>
                            <Col>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div 
                                        onClick={() => filteredApplicants.length > 0 && page > 1 ? handlePageChange(page - 1) : null}
                                        style={{ 
                                            fontFamily: 'Inter', 
                                            fontSize: '12px', 
                                            fontWeight: 400, 
                                            lineHeight: '18px', 
                                            letterSpacing: '-0.1px', 
                                            color: filteredApplicants.length > 0 && page > 1 ? '#666' : '#ccc', 
                                            cursor: filteredApplicants.length > 0 && page > 1 ? 'pointer' : 'not-allowed', 
                                            marginRight: '0px',
                                            paddingRight: '16px' 
                                        }}
                                    >
                                        ← Previous
                                    </div>
                                    <div 
                                        style={{ 
                                            borderLeft: '1px solid #d9d9d9',
                                            marginLeft: '8px',
                                            paddingLeft: '16px'
                                        }}
                                    >
                                        <div 
                                            onClick={() => filteredApplicants.length > 0 && page < Math.ceil(totalItems / pageSize) ? handlePageChange(page + 1) : null}
                                            style={{ 
                                                fontFamily: 'Inter', 
                                                fontSize: '12px', 
                                                fontWeight: 400, 
                                                lineHeight: '18px', 
                                                letterSpacing: '-0.1px', 
                                                color: filteredApplicants.length > 0 && page < Math.ceil(totalItems / pageSize) ? '#666' : '#ccc', 
                                                cursor: filteredApplicants.length > 0 && page < Math.ceil(totalItems / pageSize) ? 'pointer' : 'not-allowed' 
                                            }}
                                        >
                                            Next →
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}
                />
            </div>

            <Modal
                title="Delete Confirmation"
                open={deleteDialogOpen}
                onCancel={handleDeleteDialogClose}
                onOk={handleDeleteConfirm}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
            >
                <Typography.Text>
                    Are you sure you want to delete the application for{" "}
                    <Typography.Text strong>{selectedApplicant?.candidate_name}</Typography.Text>? 
                    This action cannot be undone.
                </Typography.Text>
            </Modal>

            {/* <Modal
                title="Confirm Email Send"
                open={emailConfirmOpen}
                onCancel={handleEmailConfirmClose}
                onOk={handleEmailConfirm}
                okText="Confirm & Send"
                cancelText="Cancel"
                confirmLoading={isSendingEmail}
            >
                <Space direction="vertical">
                    <Typography.Text>
                        You are about to send email{selectedApplicants.length > 1 ? 's' : ''} to {selectedApplicants.length} recipient{selectedApplicants.length > 1 ? 's' : ''}.
                    </Typography.Text>
                    <div style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 4 }}>
                        <Typography.Text type="secondary">
                            • {selectedApplicants.length} recipient{selectedApplicants.length > 1 ? 's' : ''} selected
                        </Typography.Text>
                        {selectionMode === 'all' && (
                            <>
                                <br />
                                <Typography.Text type="warning">
                                    ⚠️ This action will affect {selectedApplicants.length} applicants in total
                                </Typography.Text>
                            </>
                        )}
                    </div>
                </Space>
            </Modal> */}

            {openFilter && (
                <div 
                    ref={filterRef}
                    style={{
                        position: 'fixed',
                        right: '24px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1000,
                        width: "410px",
                        backgroundColor: "#FFFFFF",
                        borderRadius: "8px",
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: "80vh",
                        overflowY: "auto"
                    }}>
                        {/* Header */}
                        <div style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1000,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "16px",
                            background: "rgba(247, 247, 248, 1)",
                            borderBottom: "1px solid rgba(229, 231, 235, 1)",
                            fontFamily: "Inter",
                        }}>
                            <div style={{ 
                                display: "flex", 
                                alignItems: "center", 
                                gap: "8px",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#27272A"
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="54 54">
                                    <image xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADYAAAA2CAYAAACMRWrdAAACEUlEQVRoQ+2YPU4CQRiGZ3ahMdFAIv50FECwwxvICfQIStaEEk8AnkBsjFGCegNuAJ5AShQLChNjgEhiQaIw424hQVhhZtgPRvJtQsN+f+/7zGZnlpIlveiS6iIo7L+RRWJITBMHcClqAkJ4DCQmbJUmga7EoqnWIadk3359BzSZ888xKCN39eL67WjAmLC41dzrc1LWXdDwfKa/l6xdbleG/xsTFrPecpwb2f8kjFJ2+lTYzE0UFk/bxL6WkJij2nnGCOH2T+/LMFiHMbMk9IzpLUV8OnyPiXulRyQS04OD+BRITNwrPSKRmBuH6PFLwuQm2Ea5Vvi9/5NZC0rEEunXcPfTLDNKwzLNZGMp542VNf9uNR/sSOfKJgy2XJTfqOTK5nDaP3kubOVl85SIRax2hnJ2JttMKZ7TI7e94LRaSsISmfdA94M9MMJglyIxGsaqkazlg41pQkbvKwn7KeIccWQbisb3DV+nfhGsisZ7Kky16TzyZiI2jwFVe6AwVecWlYfEFuW8al8kpurcovJAiEVS7SwxuNDL26Ss9Hi9ce61AZ4LU9lHun3JnVUogLBW2T5uCNEaDM/Jfb0YksuZotxzYTGraX/7J1Lf/lWPJpO0eS7MabZjH2t6jB2ILCfTRyq1q1BOJFYmBkSYzABQsSgMylmoukgMylmoukgMylmoukgMylmoukgMylmoukgMylmout/YToI3olh5xwAAAABJRU5ErkJggg==" x="0" y="0" width="14" height="14"/>
                                </svg>
                                <span>Filters</span>
                            </div>
                            <CloseOutlined 
                                style={{ fontSize: "16px", cursor: "pointer", color: "#6B7280" }} 
                                onClick={() => setFilterAnchorEl(null)}
                            />
                        </div>

                        {/* Filter Content */}
                        <div style={{ padding: "16px", flexGrow: 1, fontFamily: "Inter" }}>
                            {/* Organization Section */}
                            <div style={{ marginBottom: "24px" }}>
                                <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "12px"
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M10.4965 4.90039H6.29648C5.52438 4.90039 4.89648 5.52829 4.89648 6.30039V11.9004H11.8965V6.30039C11.8965 5.52829 11.2686 4.90039 10.4965 4.90039ZM7.69648 9.80039C7.69648 10.1868 7.38358 10.5004 6.99648 10.5004C6.60938 10.5004 6.29648 10.1868 6.29648 9.80039V9.45039C6.29648 9.06399 6.60938 8.75039 6.99648 8.75039C7.38358 8.75039 7.69648 9.06399 7.69648 9.45039V9.80039ZM7.69648 7.35039C7.69648 7.73679 7.38358 8.05039 6.99648 8.05039C6.60938 8.05039 6.29648 7.73679 6.29648 7.35039V7.00039C6.29648 6.61399 6.60938 6.30039 6.99648 6.30039C7.38358 6.30039 7.69648 6.61399 7.69648 7.00039V7.35039ZM10.4965 9.80039C10.4965 10.1868 10.1836 10.5004 9.79648 10.5004C9.40938 10.5004 9.09648 10.1868 9.09648 9.80039V9.45039C9.09648 9.06399 9.40938 8.75039 9.79648 8.75039C10.1836 8.75039 10.4965 9.06399 10.4965 9.45039V9.80039ZM10.4965 7.35039C10.4965 7.73679 10.1836 8.05039 9.79648 8.05039C9.40938 8.05039 9.09648 7.73679 9.09648 7.35039V7.00039C9.09648 6.61399 9.40938 6.30039 9.79648 6.30039C10.1836 6.30039 10.4965 6.61399 10.4965 7.00039V7.35039Z" fill="#2563EB" />
												<path d="M2.79883 11.9003V3.32812C2.79883 3.01592 3.00603 2.74082 3.30633 2.65472L6.80633 1.65442C7.25363 1.52632 7.69883 1.86232 7.69883 2.32782V3.49962" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
												<path d="M2.09766 11.9004H11.8977" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
											</svg>
                                    <span style={{ fontSize: "16px" }}>By Organisation</span>
                                </div>
                                <span style={{ color: "#6B7280", fontSize: "14px" }}>
                                    ({selectedFilters.org_id ? "1" : "0"} selected)
                                </span>
                            </div>
                            <div style={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                gap: "8px",
                                maxHeight: "150px",
                                overflowY: "auto",
                                padding: "4px"
                            }}>
                                {organizations.length > 0 ? (
                                    organizations.map((org) => (
                                        <div 
                                            key={org.id}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                padding: "8px",
                                                cursor: "pointer",
                                                borderRadius: "6px",
                                                backgroundColor: selectedFilters.org_id === org.id ? "#F3F4F6" : "transparent"
                                            }}
                                            onClick={() => handleFilterChange({ target: { name: 'org_id', value: org.id } })}
                                        >
                                            <Checkbox 
                                                checked={selectedFilters.org_id === org.id}
                                                onChange={() => handleFilterChange({ target: { name: 'org_id', value: org.id } })}
                                                style={{ pointerEvents: 'none' }}
                                            />
                                            <span style={{ flex: 1 }}>{org.name}</span>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: "8px", color: "#6B7280" }}>
                                        No organizations available
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={{ width: "368px", height: "1px", backgroundColor: "#E5E7EB", margin: "8px 0" }} />

                        {/* Department Section */}
                        <div style={{ marginBottom: "24px" }}>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "12px"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
													<path d="M7.00156 4.19961H2.10156V3.14961C2.10156 2.57001 2.57196 2.09961 3.15156 2.09961H5.22706C5.46086 2.09961 5.67996 2.21651 5.80946 2.41111L7.00156 4.19961Z" fill="#2563EB" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
													<path d="M2.10156 4.19971H10.8516C11.4312 4.19971 11.9016 4.67011 11.9016 5.24971V9.79971C11.9016 10.9589 10.9608 11.8997 9.80156 11.8997H4.20156C3.04236 11.8997 2.10156 10.9589 2.10156 9.79971V4.19971Z" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
												</svg>
                                    <span style={{ fontSize: "16px" }}>By Department</span>
                                </div>
                                <span style={{ color: "#6B7280", fontSize: "14px" }}>
                                    ({selectedFilters.department_id ? "1" : "0"} selected)
                                </span>
                            </div>
                            <div style={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                gap: "8px",
                                maxHeight: "150px",
                                overflowY: "auto",
                                padding: "4px"
                            }}>
                                {selectedFilters.org_id ? (
                                    filteredDepartments.length > 0 ? (
                                        filteredDepartments.map((dept) => (
                                            <div 
                                                key={dept.id}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    padding: "8px",
                                                    cursor: "pointer",
                                                    borderRadius: "6px",
                                                    backgroundColor: selectedFilters.department_id === dept.id ? "#F3F4F6" : "transparent"
                                                }}
                                                onClick={() => handleFilterChange({ target: { name: 'department_id', value: dept.id } })}
                                            >
                                                <Checkbox 
                                                    checked={selectedFilters.department_id === dept.id}
                                                    onChange={() => handleFilterChange({ target: { name: 'department_id', value: dept.id } })}
                                                    style={{ pointerEvents: 'none' }}
                                                />
                                                <span style={{ flex: 1 }}>{dept.name}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ padding: "8px", color: "#6B7280" }}>
                                            No departments available for selected organization
                                        </div>
                                    )
                                ) : (
                                    <div style={{ padding: "8px", color: "#6B7280" }}>
                                        Please select an organization first
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={{ width: "368px", height: "1px", backgroundColor: "#E5E7EB", margin: "8px 0" }} />

                        {/* Sub-department Section */}
                        <div style={{ marginBottom: "24px" }}>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "12px"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9.45156 4.19961H7.00156L5.80946 2.41111C5.67996 2.21651 5.46086 2.09961 5.22706 2.09961H3.15156C2.57196 2.09961 2.10156 2.57001 2.10156 3.14961V9.79961C2.10156 10.3463 2.31086 10.8433 2.65386 11.2171C2.47606 10.9231 2.39836 10.5654 2.48026 10.1958L3.25796 6.69581C3.40006 6.05531 3.96846 5.59961 4.62436 5.59961H10.5016V5.24961C10.5016 4.67001 10.0312 4.19961 9.45156 4.19961Z" fill="#2563EB"/>
                                        <path d="M9.83125 11.8996H3.84765C2.95235 11.8996 2.28665 11.0701 2.48125 10.1958L3.25895 6.69581C3.40105 6.05531 3.96945 5.59961 4.62535 5.59961H10.609C11.5043 5.59961 12.4207 6.42911 12.4207 7.30341L11.1977 10.8034C11.0556 11.4439 10.4872 11.8996 9.83125 11.8996Z" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span style={{ fontSize: "16px" }}>By SubDepartment</span>
                                </div>
                                <span style={{ color: "#6B7280", fontSize: "14px" }}>
                                    ({selectedFilters.subdepartment_id ? "1" : "0"} selected)
                                </span>
                            </div>
                            <div style={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                gap: "8px",
                                maxHeight: "150px",
                                overflowY: "auto",
                                padding: "4px"
                            }}>
                                {selectedFilters.department_id ? (
                                    filteredSubDepartments.length > 0 ? (
                                        filteredSubDepartments.map((subdept) => (
                                            <div 
                                                key={subdept.id}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    padding: "8px",
                                                    cursor: "pointer",
                                                    borderRadius: "6px",
                                                    backgroundColor: selectedFilters.subdepartment_id === subdept.id ? "#F3F4F6" : "transparent"
                                                }}
                                                onClick={() => handleFilterChange({ target: { name: 'subdepartment_id', value: subdept.id } })}
                                            >
                                                <Checkbox 
                                                    checked={selectedFilters.subdepartment_id === subdept.id}
                                                    onChange={() => handleFilterChange({ target: { name: 'subdepartment_id', value: subdept.id } })}
                                                    style={{ pointerEvents: 'none' }}
                                                />
                                                <span style={{ flex: 1 }}>{subdept.name}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ padding: "8px", color: "#6B7280" }}>
                                            No sub-departments available for selected department
                                        </div>
                                    )
                                ) : (
                                    <div style={{ padding: "8px", color: "#6B7280" }}>
                                        Please select a department first
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={{ width: "368px", height: "1px", backgroundColor: "#E5E7EB", margin: "8px 0" }} />

                        {/* Role Section */}
                        <div style={{ marginBottom: "24px" }}>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "12px"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
													<path d="M6.99844 1.40039C3.91074 1.40039 1.39844 3.91269 1.39844 7.00039C1.39844 10.0881 3.91074 12.6004 6.99844 12.6004C10.0861 12.6004 12.5984 10.0881 12.5984 7.00039C12.5984 3.91269 10.0861 1.40039 6.99844 1.40039ZM6.99844 4.20039C7.96304 4.20039 8.74844 4.98579 8.74844 5.95039C8.74844 6.91499 7.96304 7.70039 6.99844 7.70039C6.03384 7.70039 5.24844 6.91499 5.24844 5.95039C5.24844 4.98579 6.03384 4.20039 6.99844 4.20039ZM6.99844 11.2004C5.93304 11.2004 4.96284 10.7986 4.22154 10.1427C4.86554 9.28309 5.87424 8.75039 6.99844 8.75039C8.12264 8.75039 9.13204 9.28309 9.77534 10.1427C9.03404 10.7986 8.06314 11.2004 6.99844 11.2004Z" fill="#2563EB" />
												</svg>
                                    <span style={{ fontSize: "16px" }}>By Role</span>
                                </div>
                                <span style={{ color: "#6B7280", fontSize: "14px" }}>
                                    ({selectedFilters.role_id ? "1" : "0"} selected)
                                </span>
                            </div>
                            <div style={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                gap: "8px",
                                maxHeight: "150px",
                                overflowY: "auto",
                                padding: "4px"
                            }}>
                                {selectedFilters.subdepartment_id ? (
                                    filteredRoles.length > 0 ? (
                                        filteredRoles.map((role) => (
                                            <div 
                                                key={role.id}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    padding: "8px",
                                                    cursor: "pointer",
                                                    borderRadius: "6px",
                                                    backgroundColor: selectedFilters.role_id === role.id ? "#F3F4F6" : "transparent"
                                                }}
                                                onClick={() => handleFilterChange({ target: { name: 'role_id', value: role.id } })}
                                            >
                                                <Checkbox 
                                                    checked={selectedFilters.role_id === role.id}
                                                    onChange={() => handleFilterChange({ target: { name: 'role_id', value: role.id } })}
                                                    style={{ pointerEvents: 'none' }}
                                                />
                                                <span style={{ flex: 1 }}>{role.name || role.role_name || role.title || "Unnamed Role"}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ padding: "8px", color: "#6B7280" }}>
                                            No roles available for selected sub-department
                                        </div>
                                    )
                                ) : (
                                    <div style={{ padding: "8px", color: "#6B7280" }}>
                                        Please select a sub-department first
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={{ width: "368px", height: "1px", backgroundColor: "#E5E7EB", margin: "8px 0" }} />

                        {/* Job Opening Section */}
                        <div style={{ marginBottom: "24px" }}>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "12px"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8.86473 5.4286C8.58428 5.4286 8.35667 5.20099 8.35667 4.92054V3.22699C8.35667 3.1335 8.28079 3.05763 8.18731 3.05763H5.81634C5.72286 3.05763 5.64699 3.1335 5.64699 3.22699V4.92054C5.64699 5.20099 5.41938 5.4286 5.13892 5.4286C4.85847 5.4286 4.63086 5.20099 4.63086 4.92054V3.22699C4.63086 2.57328 5.16263 2.0415 5.81634 2.0415H8.18731C8.84102 2.0415 9.3728 2.57328 9.3728 3.22699V4.92054C9.3728 5.20099 9.14518 5.4286 8.86473 5.4286Z" fill="#2563EB" />
                                        <path d="M12.4207 6.79644V6.2755C12.4207 5.24786 11.5855 4.4126 10.5578 4.4126H3.44493C2.41729 4.4126 1.58203 5.24786 1.58203 6.2755V6.79644C3.06152 7.57479 4.72729 8.03611 6.49332 8.11266V7.96905C6.49332 7.6886 6.72093 7.46099 7.00139 7.46099C7.28184 7.46099 7.50945 7.6886 7.50945 7.96905V8.11266C9.27548 8.03611 10.9413 7.57479 12.4207 6.79644Z" fill="#2563EB" />
                                        <path d="M7.50945 9.12915V9.4936C7.50945 9.77405 7.28184 10.0017 7.00139 10.0017C6.72093 10.0017 6.49332 9.77405 6.49332 9.4936V9.12915C4.74152 9.05937 3.08319 8.63599 1.58203 7.92334V10.6791C1.58203 11.7067 2.41729 12.542 3.44493 12.542H10.5578C11.5855 12.542 12.4207 11.7067 12.4207 10.6791V7.92334C10.9196 8.63531 9.26126 9.05937 7.50945 9.12915Z" fill="#2563EB" />
                                    </svg>
                                    <span style={{ fontSize: "16px" }}>By Job Opening</span>
                                </div>
                                <span style={{ color: "#6B7280", fontSize: "14px" }}>
                                    ({selectedFilters.job_opening_id ? "1" : "0"} selected)
                                </span>
                            </div>
                            <div style={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                gap: "8px",
                                maxHeight: "150px",
                                overflowY: "auto",
                                padding: "4px"
                            }}>
                                {selectedFilters.role_id ? (
                                    filteredJobOpenings.length > 0 ? (
                                        filteredJobOpenings.map((job) => (
                                            <div 
                                                key={job.id}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    padding: "8px",
                                                    cursor: "pointer",
                                                    borderRadius: "6px",
                                                    backgroundColor: selectedFilters.job_opening_id === job.id ? "#F3F4F6" : "transparent"
                                                }}
                                                onClick={() => handleFilterChange({ target: { name: 'job_opening_id', value: job.id } })}
                                            >
                                                <Checkbox 
                                                    checked={selectedFilters.job_opening_id === job.id}
                                                    onChange={() => handleFilterChange({ target: { name: 'job_opening_id', value: job.id } })}
                                                    style={{ pointerEvents: 'none' }}
                                                />
                                                <span style={{ flex: 1 }}>{job.name || job.title || job.jobopening_name || "Unnamed Job Opening"}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ padding: "8px", color: "#6B7280" }}>
                                            No job openings available for selected role
                                        </div>
                                    )
                                ) : (
                                    <div style={{ padding: "8px", color: "#6B7280" }}>
                                        Please select a role first
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={{ width: "368px", height: "1px", backgroundColor: "#E5E7EB", margin: "8px 0" }} />

                        {/* AI Score Range Section */}
                        <div style={{ marginBottom: "24px" }}>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "12px"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M7 13L13 7L7 1L1 7L7 13Z" fill="#2563EB" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span style={{ fontSize: "16px" }}>By AI Score Range</span>
                                </div>
                                <span style={{ color: "#6B7280", fontSize: "14px" }}>
                                    {(selectedFilters.ai_score_min || selectedFilters.ai_score_max) ? "Range set" : "No range"}
                                </span>
                            </div>
                            <div style={{ 
                                display: "flex", 
                                gap: "8px",
                                padding: "4px"
                            }}>
                                <Input
                                    placeholder="Min Score"
                                    type="number"
                                    name="ai_score_min"
                                    value={selectedFilters.ai_score_min}
                                    onChange={handleFilterChange}
                                    style={{ width: "50%" }}
                                />
                                <Input
                                    placeholder="Max Score"
                                    type="number"
                                    name="ai_score_max"
                                    value={selectedFilters.ai_score_max}
                                    onChange={handleFilterChange}
                                    style={{ width: "50%" }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{ 
                        padding: "16px",
                        display: "flex",
                        gap: "10px",
                        background: "rgba(238, 238, 240, 1)",
                        borderTop: "0.5px solid rgba(218, 218, 221, 1)",
                        borderRadius: '8px 8px',
                        position: "sticky",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        fontFamily: "Inter"
                    }}>
                        <img 
                            src={cancelIcon} 
                            onClick={handleClearFilters}
                            alt="cancel" 
                            style={{ 
                                width: "180px",
                                height: "48px",
                                objectFit: "contain",
                                cursor: "pointer",
                                transition: "transform 0.2s",
                                marginBottom: "-4px"
                            }}
                            onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.95)"}
                            onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
                            onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                        />
                        
                        <img 
                            src={saveIcon} 
                            onClick={applyFilters}
                            alt="save" 
                            style={{ 
                                width: "180px",
                                height: "39px",
                                objectFit: "contain",
                                cursor: "pointer",
                                transition: "transform 0.2s"
                            }}
                            onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.95)"}
                            onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
                            onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApplicantDataTable; 